/**
 * Contrato de entrada do Pro: duração, locais obrigatórios e destinos.
 *
 * Módulo puro (sem rede, sem IA) e isolado do Premium. A leitura do pedido
 * corre SEMPRE por esta ordem sobre o texto original:
 *
 *   1. extrair a duração explícita ("durante 3 dias" → 3);
 *   2. remover apenas a expressão de duração;
 *   3. separar a cláusula de locais obrigatórios ("que inclua …");
 *   4. extrair os `requiredStops` dessa cláusula;
 *   5. extrair destinos apenas do texto restante ANTES da cláusula.
 *
 * Assim "durante 3 dias" nunca entra no nome do último local obrigatório e
 * nunca desaparece por a cláusula de inclusão ter sido removida.
 */

import { extractDestinationFromPrompt } from "../destination-extraction";

export type DurationSource = "text" | "manual";

export type ProRequestContract = {
  /** Bases reais do pedido (nunca POIs, nunca palavras funcionais). */
  destinationBases: string[];
  /** Locais nomeados que o utilizador exigiu ("que inclua …"). */
  requiredStops: string[];
  /** Dias vinculativos: duração explícita do texto ou edição manual. */
  totalDays: number | null;
  daysSource: DurationSource | null;
};

const NUMBER_WORDS: Record<string, number> = {
  um: 1, uma: 1, dois: 2, duas: 2, tres: 3, quatro: 4, cinco: 5, seis: 6, sete: 7,
  oito: 8, nove: 9, dez: 10, onze: 11, doze: 12, quinze: 15,
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
};

const DURATION_UNIT =
  "(?:dias?|d[ií]as?|days?|noites?|nights?|giorni?|notti?|jours?|nuits?|tage?|n[aä]chte)";

const LEAD = "(?:durante|por|para|ao\\s+longo\\s+de|during|for|in)";

/** Só conta uma duração colada a uma unidade temporal explícita. */
const DURATION_RE = new RegExp(
  `(?:(?<![\\p{L}\\p{N}])${LEAD}\\s+)?(?<![\\p{L}\\p{N}])(\\d{1,2}|\\p{L}{2,6})\\s*${DURATION_UNIT}(?![\\p{L}\\p{N}])`,
  "iu",
);

function normalize(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .replace(/\s+/gu, " ");
}

/**
 * Palavras funcionais do pedido: nunca podem virar cidade, mesmo que um
 * fornecedor geográfico devolva uma localidade com nome parecido.
 */
export const FUNCTIONAL_REQUEST_WORDS = new Set([
  "rota", "rotas", "roteiro", "roteiros", "viagem", "viagens", "plano", "planos",
  "itinerario", "itinerarios", "tour", "tours", "trip", "trips", "route", "routes",
  "itinerary", "itineraries", "plan", "plans", "percurso", "percursos",
]);

export function isFunctionalRequestWord(value: string): boolean {
  return FUNCTIONAL_REQUEST_WORDS.has(normalize(value));
}

export type ExplicitDuration = { days: number; start: number; end: number };

/** Duração explícita ("3 dias", "durante 3 dias"), nunca de nomes próprios. */
export function extractExplicitDuration(text: string): ExplicitDuration | null {
  if (!text?.trim()) return null;
  const match = DURATION_RE.exec(text);
  if (!match || match.index === undefined) return null;
  const raw = match[1] ?? "";
  const days = /^\d+$/u.test(raw) ? Number(raw) : (NUMBER_WORDS[normalize(raw)] ?? 0);
  if (!Number.isFinite(days) || days < 1 || days > 60) return null;
  return { days, start: match.index, end: match.index + match[0].length };
}

/** Remove apenas a expressão de duração (mantém tudo o resto intacto). */
export function stripDurationExpression(text: string): string {
  const found = extractExplicitDuration(text);
  if (!found) return text;
  return `${text.slice(0, found.start)} ${text.slice(found.end)}`
    .replace(/\s{2,}/gu, " ")
    .replace(/\s+([,;.!?])/gu, "$1")
    .trim();
}

const REQUIRED_CLAUSE_RE =
  /(?:^|[\s,;])(?:que\s+inclu[aiy]\w*|que\s+contenha\w*|inclu[ií]ndo|inclua\w*|incluir|com\s+paragem\s+em|passar\s+p(?:or|elos?|elas?)|passando\s+por|including|that\s+includes?|include\s+|che\s+includa|con\s+parada\s+en)\s+/iu;

export type RequiredStopsSplit = { before: string; clause: string };

/** Separa o texto na cláusula de locais obrigatórios. */
export function splitRequiredStopsClause(text: string): RequiredStopsSplit {
  const match = REQUIRED_CLAUSE_RE.exec(text ?? "");
  if (!match || match.index === undefined) return { before: text ?? "", clause: "" };
  return {
    before: text.slice(0, match.index).trim(),
    clause: text.slice(match.index + match[0].length).trim(),
  };
}

const STOP_TOKENS = new Set([
  "", "e", "and", "y", "et", "und", "o", "a", "os", "as", "um", "uma", "the",
  "la", "le", "el", "tambem", "tambien", "also", "etc", "ainda", "depois",
]);

const LEADING_ARTICLES = /^(?:a|o|as|os|à|às|ao|aos|na|no|nas|nos|em|pela|pelo|pelas|pelos|por|the|el|la|los|las)\s+/iu;

/** Lê a lista de locais obrigatórios de uma cláusula já isolada. */
export function extractRequiredStops(clause: string): string[] {
  if (!clause?.trim()) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  const parts = clause
    .split(/[,;\n]+|\s+(?:e|and|y|et|und)\s+/iu)
    .map((part) => part.replace(/[.!?]+\s*$/u, "").trim());
  for (const part of parts) {
    const cleaned = part.replace(LEADING_ARTICLES, "").trim();
    if (cleaned.length < 2 || cleaned.length > 80) continue;
    if (!/\p{L}/u.test(cleaned)) continue;
    if (STOP_TOKENS.has(normalize(cleaned))) continue;
    if (isFunctionalRequestWord(cleaned)) continue;
    const key = normalize(cleaned);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(cleaned);
    if (out.length >= 12) break;
  }
  return out;
}

/**
 * Contrato completo do pedido Pro. `formDays` só entra quando o utilizador
 * editou manualmente o campo Dias (`formDaysSource === "manual"`), e nesse
 * caso vence a duração escrita.
 */
export function parseProRequestContract(
  text: string,
  options: { formDays?: number; formDaysSource?: DurationSource | null } = {},
): ProRequestContract {
  const source = text ?? "";
  const duration = extractExplicitDuration(source);
  const withoutDuration = stripDurationExpression(source);
  const { before, clause } = splitRequiredStopsClause(withoutDuration);
  const requiredStops = extractRequiredStops(clause);
  const destinationText = before || withoutDuration;

  const bases: string[] = [];
  const extracted = extractDestinationFromPrompt(destinationText);
  if (extracted.city && !isFunctionalRequestWord(extracted.city)) bases.push(extracted.city);

  const manual =
    options.formDaysSource === "manual" && (options.formDays ?? 0) >= 1
      ? Math.floor(options.formDays as number)
      : null;
  const totalDays = manual ?? duration?.days ?? null;
  const daysSource = manual ? "manual" : duration ? "text" : null;

  return { destinationBases: bases, requiredStops, totalDays, daysSource };
}
